import _ from 'underscore';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { stringifySync } from '@bingads-webui/safe-json';
import {
  CONTROL_TYPES,
  URL_OPTION_TYPES,
  NETWORK_TYPES,
  PERIOD_TYPE,
  LANGUAGE_TYPES,
  REQUEST_SUPPORTED_LANGUAGES,
  DeviceTypes,
  MATCH_TYPE,
  CHANNEL_TYPES,
  CHANNEL_TYPES_MAPPING,
} from './consts';
import { defaultIdeaOverview, defaultDevicesTypeIds } from './config';

export const getURLDomain = url =>
  (url.replace('http://', '').replace('https://', '').split(/[/?#]/)[0]);

export const isJsonEqual = (v1, v2) => (JSON.stringify(v1) === JSON.stringify(v2));

export const formatUrl = ({ url, scopeValues }) => url
  .replace('{aid}', scopeValues.aid)
  .replace('{cid}', scopeValues.cid);

export const getOptions = ({
  token,
  lcid,
  data = {},
  channel = CHANNEL_TYPES.KEYWORD_PLANNER_TOOL,
}) => ({
  contentType: 'application/json',
  type: _.isEmpty(data) ? 'GET' : 'POST',
  headers: {
    authorization: `CCMTSmallToken ${token}`,
    'x-ms-lcid': `${lcid}`,
    'x-ms-scenarioname': CHANNEL_TYPES_MAPPING[channel],
    scenario: channel,
  },
  data: _.isEmpty(data) ? null : stringifySync(data),
});

export const getRequestSupportedLocaleId = ({ localeString }) => {
  // Check prefix of localeString to determine language
  let parsedLanguage = _.find(REQUEST_SUPPORTED_LANGUAGES, (supportedLanguage) => {
    const languageRegex = new RegExp(`^${supportedLanguage.prefix}(-.+)?$`);
    return languageRegex.test(localeString);
  });

  // Add switch case here if special cases needed
  // Fall back to use English
  if (_.isUndefined(parsedLanguage)) {
    parsedLanguage = REQUEST_SUPPORTED_LANGUAGES[LANGUAGE_TYPES.ENGLISH];
  }
  return parsedLanguage.lcid;
};

export const redirectToLogin = () => {
  window.location.href = `${window.location.protocol}//${window.location.host}/default.aspx?reason=1&msg=sessionexpired`;
};

export const formatProducts = (model) => {
  let productsModel;
  if (model.isIdeas) {
    productsModel = model[CONTROL_TYPES.SEARCH_IDEA_PRODUCTS];
  } else {
    productsModel = !_.isEmpty(model[CONTROL_TYPES.SEARCH_VOLUME_PRODUCTS].parsedKeywords) ?
      model[CONTROL_TYPES.SEARCH_VOLUME_PRODUCTS] :
      model[CONTROL_TYPES.SEARCH_VOLUME_UPLOAD_PRODUCTS];
  }
  return !_.isEmpty(productsModel.parsedKeywords) ?
    productsModel.parsedKeywords : undefined;
};

export const formatLandingPageURL = landingPage =>
  (landingPage.option === URL_OPTION_TYPES.DOMAIN ?
    getURLDomain(landingPage.url) : landingPage.url);

export const formatTargetings = (model, isLanguageDecoupling = false, isLanguageSkipped = false) => {
  const targetings = [];
  // Device
  _.each(defaultDevicesTypeIds, deviceType => targetings.push({
    '@odata.type': '#Model.TrafficEstimate.DeviceTargeting',
    DeviceType: deviceType,
    AdjustmentPercantage: 0,
  }));

  // URL Option
  if (model.isIdeas && !_.isEmpty(model[CONTROL_TYPES.LANDING_PAGE].url)) {
    targetings.push({
      '@odata.type': `#Model.Suggestions.${CONTROL_TYPES.LANDING_PAGE}Targeting`,
      SuggestionType: model[CONTROL_TYPES.LANDING_PAGE].option,
    });
  }

  // Language
  if (!isLanguageSkipped && !_.isEmpty(model[CONTROL_TYPES.LANGUAGE])) {
    targetings.push({
      '@odata.type': `#Model.TrafficEstimate.${CONTROL_TYPES.LANGUAGE}Targeting`,
      Language: model[CONTROL_TYPES.LANGUAGE],
    });
  }

  // Location
  if (!_.isEmpty(model[isLanguageDecoupling ? CONTROL_TYPES.LOCATION_LD : CONTROL_TYPES.LOCATION])) {
    const locations = _.uniq(model[isLanguageDecoupling ? CONTROL_TYPES.LOCATION_LD : CONTROL_TYPES.LOCATION], false, item => item.id);
    _.each(locations, (item) => {
      targetings.push({
        '@odata.type': `#Model.TrafficEstimate.${CONTROL_TYPES.LOCATION}Targeting`,
        LocationId: item.id,
      });
    });
  }
  // Network
  if (!_.isEmpty(model[CONTROL_TYPES.NETWORK])) {
    if (model[CONTROL_TYPES.NETWORK] === NETWORK_TYPES.ALL) {
      _.each(NETWORK_TYPES, (networkType) => { // list all network types for targeting
        if (networkType !== NETWORK_TYPES.ALL) {
          targetings.push({
            '@odata.type': `#Model.TrafficEstimate.${CONTROL_TYPES.NETWORK}Targeting`,
            NetworkType: networkType,
          });
        }
      });
    } else {
      targetings.push({
        '@odata.type': `#Model.TrafficEstimate.${CONTROL_TYPES.NETWORK}Targeting`,
        NetworkType: model[CONTROL_TYPES.NETWORK],
      });
    }
  }
  return targetings;
};

export const formatFilters = ({ model, scopeValues, isCategoryFilter }) => {
  const filters = [];
  // Average Search Volume Range
  if (model[CONTROL_TYPES.KEYWORD_FILTERS_GROUP][CONTROL_TYPES.AVERAGE_MONTHLY_SEARCH].value) {
    const range = model[CONTROL_TYPES.KEYWORD_FILTERS_GROUP][CONTROL_TYPES.AVERAGE_MONTHLY_SEARCH];
    filters.push({
      '@odata.type': `#Model.Suggestions.${CONTROL_TYPES.AVERAGE_MONTHLY_SEARCH}Filter`,
      AverageSearchVolumeRange: {
        [range.operator]: range.value,
      },
    });
  }
  // Suggested Bid Range
  if (model[CONTROL_TYPES.KEYWORD_FILTERS_GROUP][CONTROL_TYPES.SUGGESTED_BID].value) {
    const range = model[CONTROL_TYPES.KEYWORD_FILTERS_GROUP][CONTROL_TYPES.SUGGESTED_BID];
    filters.push({
      '@odata.type': `#Model.Suggestions.${CONTROL_TYPES.SUGGESTED_BID}Filter`,
      SuggestedBidRange: {
        [range.operator]: {
          Amount: range.value,
        },
      },
    });
  }
  // Ad Impression Share Range
  if (model[CONTROL_TYPES.KEYWORD_FILTERS_GROUP][CONTROL_TYPES.AD_IMPRESSION_SHARE].value) {
    const range = model[CONTROL_TYPES.KEYWORD_FILTERS_GROUP][CONTROL_TYPES.AD_IMPRESSION_SHARE];
    filters.push({
      '@odata.type': `#Model.Suggestions.${CONTROL_TYPES.AD_IMPRESSION_SHARE}Filter`,
      AdImpressionShareRange: {
        [range.operator]: range.value,
      },
    });
  }
  // Competition
  if (!_.isEmpty(model[CONTROL_TYPES.KEYWORD_FILTERS_GROUP][CONTROL_TYPES.COMPETITION])) {
    filters.push({
      '@odata.type': `#Model.Suggestions.${CONTROL_TYPES.COMPETITION}Filter`,
      CompetitionLevels: model[CONTROL_TYPES.KEYWORD_FILTERS_GROUP][CONTROL_TYPES.COMPETITION],
    });
  }

  // Included and excluded keywords
  if (!_.isEmpty(model[CONTROL_TYPES.INCLUDED_KEYWORD].parsedKeywords) ||
    !_.isEmpty(model[CONTROL_TYPES.NEGATIVE_KEYWORD].parsedKeywords)) {
    filters.push({
      '@odata.type': '#Model.Suggestions.IncludedAndExcludedFilter',
      Included: model[CONTROL_TYPES.INCLUDED_KEYWORD].parsedKeywords,
      Excluded: model[CONTROL_TYPES.NEGATIVE_KEYWORD].parsedKeywords,
    });
  }

  // Advertiser
  if (!model[CONTROL_TYPES.ACCOUNT_KEYWORD_DEDUPE]) {
    filters.push({
      '@odata.type': '#Model.Suggestions.AdvertiserFilter',
      AdvertiserId: scopeValues.aid,
      AdvertiserLevel: 'Account',
    });
  }

  // Category
  if (model[CONTROL_TYPES.CATEGORY].categoryId && isCategoryFilter) {
    filters.push({
      '@odata.type': '#Model.Suggestions.CategoryFilter',
      Category: Number(model[CONTROL_TYPES.CATEGORY].categoryId),
    });
  }

  return filters;
};

export const formatPeriod = (model) => {
  const periodSetting = {};
  // date range
  if (!_.isEmpty(model[CONTROL_TYPES.DATE_RANGE])) {
    periodSetting.PeriodType = PERIOD_TYPE.MONTHLY;

    const start = new Date(model[CONTROL_TYPES.DATE_RANGE].start);
    const end = new Date(model[CONTROL_TYPES.DATE_RANGE].end);

    periodSetting.DateRange = {
      Start: start.toISOString(),
      End: end.toISOString(),
    };
  }
  return periodSetting;
};

export const formatTrafficEstimateNegatieKeywords = model =>
  _.map(
    model[CONTROL_TYPES.NEGATIVE_KEYWORD].parsedKeywords,
    keyword => ({
      Text: keyword,
      MatchType: MATCH_TYPE.PHRASE, // hard code for MT
    })
  );

export const formatIdeaOverviews = (model, mergeLocation) => {
  const ideaOverviews = mergeLocation ? [] : _.clone(defaultIdeaOverview);
  if (!_.isEmpty(model[CONTROL_TYPES.LOCATION_LEVEL])) {
    ideaOverviews.push({
      IdeaAttribute: 'AverageSearchVolume',
      Segment: {
        '@odata.type': `#Model.Suggestions.${CONTROL_TYPES.LOCATION}Segment`,
        LocationLevel: model[CONTROL_TYPES.LOCATION_LEVEL],
      },
    });
  }
  return ideaOverviews;
};

export const cleanTrafficEstKeyword = (adgroups) => {
  const newAdgroups =
    _.map(adgroups, adGroupTrafficEstimateParameter => (
      _.extend(
        adGroupTrafficEstimateParameter,
        {
          KeywordTrafficEstimateParameters: _.map(
            adGroupTrafficEstimateParameter.KeywordTrafficEstimateParameters,
            keyword => ({
              ..._.pick(keyword, 'MatchType', 'Text', 'MaxCPC'),
            })
          ),
        }
      )));
  return newAdgroups;
};

export const formatAdGroupTrafficEstimateParameters = (model) => {
  let AdGroupTrafficEstimateParameters =
    _.toArray(cloneDeep(model[CONTROL_TYPES.TRAFFIC_ESTIMATE_ADGROUP_KEYWORDS]));
  AdGroupTrafficEstimateParameters = cleanTrafficEstKeyword(AdGroupTrafficEstimateParameters);
  const hasBid = !!model[CONTROL_TYPES.TRAFFIC_ESTIMATE_BID];
  const hasInlineCustomBid = model.isTrafficEstScale;

  if (hasBid || hasInlineCustomBid) {
    AdGroupTrafficEstimateParameters =
      _.map(AdGroupTrafficEstimateParameters, adGroupTrafficEstimateParameter => (
        _.extend(
          adGroupTrafficEstimateParameter,
          {
            MaxCPC: model.trafficEstLandscapeScale *
              (adGroupTrafficEstimateParameter.MaxCPC || model[CONTROL_TYPES.TRAFFIC_ESTIMATE_BID])
              || undefined,
            KeywordTrafficEstimateParameters: _.map(
              adGroupTrafficEstimateParameter.KeywordTrafficEstimateParameters,
              keyword => ({
                ...keyword,
                MaxCPC: model.trafficEstLandscapeScale * keyword.MaxCPC || undefined,
              })
            ),
          }
        )));
  }

  return {
    AdGroupTrafficEstimateParameters,
    scaleLandScape: hasInlineCustomBid,
    // TODO: if bid change && scale mode, request landscape, else do not request landscape
    requestLandscape: true,
  };
};

export const formatLocationLevels = model => (!_.isEmpty(model[CONTROL_TYPES.LOCATION_LEVEL]) ?
  [model[CONTROL_TYPES.LOCATION_LEVEL]] : []);

export const formatAdGroupEstimatedTraffics = (model) => {
  const adGroupEstimatedTraffics =
    _.map(
      _.toArray(model[CONTROL_TYPES.TRAFFIC_ESTIMATE_ADGROUP_KEYWORDS]),
      adgroupKeywords => ({
        AdGroupName: adgroupKeywords.AdGroupName,
        KeywordEstimatedTraffics: adgroupKeywords.KeywordTrafficEstimateParameters,
        MaxCPC: adgroupKeywords.MaxCPC && adgroupKeywords.MaxCPC * model.trafficEstLandscapeScale,
        HasOwnMaxCPC: !!adgroupKeywords.MaxCPC,
      })
    );

  return adGroupEstimatedTraffics;
};

export const formatAdGroupEstimatedTrafficsForMaxCPC = (model, adGroupEstimatedTraffics) => {
  const adgroups = model[CONTROL_TYPES.TRAFFIC_ESTIMATE_ADGROUP_KEYWORDS];
  const emptyTrafficTemplate = {
    Clicks: 0.0,
    Impressions: 0.0,
    TotalCost: 0.0,
  };
  const values = _.map(adGroupEstimatedTraffics, entity => ({
    AdGroupName: entity.AdGroupName,
    MaxCPC: model.trafficEstLandscapeScale *
      ((adgroups[entity.AdGroupName] && adgroups[entity.AdGroupName].MaxCPC) ||
        model[CONTROL_TYPES.TRAFFIC_ESTIMATE_BID]),
    HasOwnMaxCPC: adgroups[entity.AdGroupName] && adgroups[entity.AdGroupName].MaxCPC,
    // Check if the traffics are actually empty
    AdGroupEstimatedTraffic: _.isMatch(entity.AdGroupEstimatedTraffic, emptyTrafficTemplate) ?
      _.clone(emptyTrafficTemplate) : entity.AdGroupEstimatedTraffic,
    KeywordEstimatedTraffics: _.map(entity.KeywordEstimatedTraffics, traffic =>
      (_.isMatch(traffic.KeywordEstimatedTraffic, emptyTrafficTemplate) ?
        _.extend(traffic, { KeywordEstimatedTraffic: _.clone(emptyTrafficTemplate) }) : traffic)),
  }));
  return values;
};

export const formatDeviceTraffics = () => {
  const deviceList = [DeviceTypes.Desktop, DeviceTypes.Mobile, DeviceTypes.Tablet];
  const devices = _.map(deviceList, deviceName => ({
    Device: { DeviceName: deviceName },
  }));
  return devices;
};

export const formatDeviceTrafficsWithBid = (deviceTraffics) => {
  const deviceList = [DeviceTypes.Desktop, DeviceTypes.Mobile, DeviceTypes.Tablet];
  return !_.isEmpty(deviceTraffics) ?
    deviceTraffics :
    _.map(deviceList, deviceName => ({
      Device: { DeviceName: deviceName },
      Traffic: {
        Clicks: 0.0,
        Impressions: 0.0,
        TotalCost: 0.0,
      },
    }));
};

export const formatLocationTraffics = (model) => {
  const locationList = model.Location;
  const locations = _.map(locationList, location => ({
    Location: { LocationName: location.name },
  }));
  return [{ LocationTraffics: locations }];
};

export const formatLocationTrafficsWithBid = (model, locationTraffics) => {
  const locationList = model.Location;
  return !_.isEmpty(locationTraffics) && !_.isEmpty(locationTraffics[0].LocationTraffics) ?
    locationTraffics :
    [{
      LocationTraffics: _.map(locationList, location => ({
        Location: { LocationName: location.name },
        Traffic: {
          Clicks: 0.0,
          Impressions: 0.0,
          TotalCost: 0.0,
        },
      })),
    }];
};

export const formatKeywordEstimatedTraffics = (adGroupEstimatedTraffics, model) => {
  const values = [];
  const adgroups = model[CONTROL_TYPES.TRAFFIC_ESTIMATE_ADGROUP_KEYWORDS];
  _.each(adGroupEstimatedTraffics, (adGroupItem) => {
    _.each(adGroupItem.KeywordEstimatedTraffics, (keywordItem) => {
      const modelAdgroup = adgroups[adGroupItem.AdGroupName];
      const modelKeyword = _.find(
        modelAdgroup && modelAdgroup.KeywordTrafficEstimateParameters,
        item => item.Text === keywordItem.Text
      ) || {};
      values.push({
        AdGroupName: adGroupItem.AdGroupName,
        Text: keywordItem.Text,
        MatchType: keywordItem.MatchType,
        KeywordEstimatedTraffic: keywordItem.KeywordEstimatedTraffic,
        MaxCPC: model.trafficEstLandscapeScale *
          (modelKeyword.MaxCPC || (modelAdgroup && modelAdgroup.MaxCPC)
            || model[CONTROL_TYPES.TRAFFIC_ESTIMATE_BID]) || undefined,
        HasOwnMaxCPC: !!modelKeyword.MaxCPC,
      });
    });
  });
  return values;
};

export const normalizeTrafficEstimate = ({ model, rawData, i18n }) => {
  // Backfill the default value from input when bid value or keyword is empty
  const isBackfilled = !model[CONTROL_TYPES.TRAFFIC_ESTIMATE_BID] ||
    _.isEmpty(model[CONTROL_TYPES.TRAFFIC_ESTIMATE_ADGROUP_KEYWORDS]);
  const value = _.first(rawData.value);
  const backfilledValues = {
    value: [{
      Identifier: value.Identifier,
      AdGroupEstimatedTrafficRanges: value.AdGroupEstimatedTrafficRanges,
      AdGroupEstimatedTraffics: isBackfilled ?
        formatAdGroupEstimatedTraffics(model) :
        formatAdGroupEstimatedTrafficsForMaxCPC(
          model,
          value.AdGroupEstimatedTraffics
        ),
      DeviceTraffics: isBackfilled ?
        formatDeviceTraffics(i18n) :
        formatDeviceTrafficsWithBid(value.DeviceTraffics),
      LocationTraffics: isBackfilled ?
        formatLocationTraffics(model) :
        formatLocationTrafficsWithBid(model, value.LocationTraffics),
      LandscapesEstimate: value.LandscapesEstimate,
      KeywordEstimatedTraffics: isBackfilled ?
        formatKeywordEstimatedTraffics(formatAdGroupEstimatedTraffics(model), model) :
        formatKeywordEstimatedTraffics(value.AdGroupEstimatedTraffics, model),
    }],
  };
  const normalizedData = _.extend({}, rawData, backfilledValues);

  return normalizedData;
};
